import { useRef, useState, useEffect, useCallback } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import ExperienceCard from './ExperienceCard';
import { resolveIcon } from './ExperienceIcons';
import { useConfig } from '../../context/useConfig';
import { horizontalEntrance } from '../../utils/motionVariants';

/* ─── SVG path builder ─────────────────────────────────────────────────────
   Produces a smooth S-curve through all nodes using the "horizontal-tangent"
   cubic bezier construction:

     C (cx ± sway, y_curr)  (cx ± sway, y_next)  (cx, y_next)

   At every node the tangent is purely horizontal: (±sway, 0).
   Because the tangent direction is independent of vertical spacing, C1
   continuity is guaranteed even when card heights are unequal — fixing the
   sharp corners that appear at unevenly-spaced nodes.
─────────────────────────────────────────────────────────────────────────── */
function buildJourneyPath(nodeYs, containerW) {
  if (!nodeYs || nodeYs.length < 1) return '';
  const cx   = containerW / 2;
  // Peak x-deviation = 0.75 × sway (cubic bezier midpoint formula).
  // Target ~155px peak at typical 960px container width → sway ≈ 205px.
  // Floor at 120px so the curve is still visible on narrower containers.
  const sway = Math.max(120, Math.min(containerW * 0.22, 210));

  // ── Entry: drop straight down from the very top, then arc into node 0 with the
  //    same rightward horizontal tangent as the first main segment (+sway, 0).
  const y0   = nodeYs[0];
  const topY = 0;
  // CP1 keeps the path vertical at the top; CP2 = (cx - sway, y0) gives the
  // incoming tangent direction = (sway, 0) — matching segment 0's outgoing direction.
  let d = `M ${cx} ${topY} C ${cx} ${y0 * 0.5} ${cx - sway} ${y0} ${cx} ${y0}`;

  // ── Main segments ─────────────────────────────────────────────────────────
  //    Segment i bows to side sign*sway.  Tangent at start and end:
  //      start → (+sign*sway, 0)   end → (-sign*sway, 0)
  //    Adjacent segments share the same horizontal tangent direction at the
  //    shared node, so the junction is always C1 smooth.
  for (let i = 0; i < nodeYs.length - 1; i++) {
    const bx = cx + (i % 2 === 0 ? sway : -sway);
    d += ` C ${bx} ${nodeYs[i]} ${bx} ${nodeYs[i + 1]} ${cx} ${nodeYs[i + 1]}`;
  }

  // ── Tail: depart from last node matching its incoming horizontal tangent
  //    Last segment index = nodeYs.length - 2; its sign = (len-2)%2===0 ? 1 : -1.
  //    Its end tangent = (-sign*sway, 0), so tail CP1 = (cx - sign*sway, lastY).
  const lastY       = nodeYs[nodeYs.length - 1];
  const lastSegSign = nodeYs.length <= 1 ? 1 : (nodeYs.length - 2) % 2 === 0 ? 1 : -1;
  const tailY       = lastY + 48;
  // CP1 departs with the same horizontal tangent as the incoming direction; CP2 brings it vertical.
  d += ` C ${cx - lastSegSign * sway} ${lastY} ${cx} ${tailY} ${cx} ${tailY}`;

  return d;
}

/* ─── Main component ─────────────────────────────────────────────────────── */
const ExperienceTimeline = () => {
  const { config } = useConfig();
  const experiences = Array.isArray(config.experience) ? config.experience : [];
  const reduceMotion = useReducedMotion();

  const containerRef = useRef(null);
  const nodeRefs = useRef(experiences.map(() => ({ current: null })));

  const [pathD, setPathD] = useState('');
  const [svgHeight, setSvgHeight] = useState(0);

  // Keep nodeRefs array length in sync if experiences ever change
  if (nodeRefs.current.length !== experiences.length) {
    nodeRefs.current = experiences.map(() => ({ current: null }));
  }

  const recalcPath = useCallback(() => {
    const container = containerRef.current;
    if (!container || experiences.length === 0) return;

    const containerRect = container.getBoundingClientRect();
    const nodeYs = nodeRefs.current.map((ref) => {
      if (!ref.current) return 0;
      const r = ref.current.getBoundingClientRect();
      return r.top - containerRect.top + r.height / 2;
    });

    setPathD(buildJourneyPath(nodeYs, containerRect.width));
    setSvgHeight(containerRect.height);
  }, [experiences.length]);

  useEffect(() => {
    recalcPath();
    const ro = new ResizeObserver(recalcPath);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [recalcPath]);

  return (
    <div ref={containerRef} className="relative">

      {/* ── Mobile vertical rule (left edge) ─────────────────── */}
      <div
        aria-hidden
        className="md:hidden absolute left-5 top-0 w-0.5 h-full rounded-full bg-gradient-to-b from-emerald-400 to-cyan-400 opacity-30"
      />

      {/* ── Desktop SVG curvy path ────────────────────────────── */}
      {pathD && (
        <svg
          aria-hidden
          className="hidden md:block pointer-events-none absolute inset-0 overflow-visible"
          style={{ width: '100%', height: svgHeight }}
        >
          <defs>
            <linearGradient id="journeyGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor="#10b981" stopOpacity="0.7" />
              <stop offset="50%"  stopColor="#06b6d4" stopOpacity="0.85" />
              <stop offset="100%" stopColor="#10b981" stopOpacity="0.5" />
            </linearGradient>
          </defs>
          <motion.path
            d={pathD}
            stroke="url(#journeyGrad)"
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
            initial={{ pathLength: 0, opacity: 0 }}
            whileInView={{ pathLength: reduceMotion ? 1 : 1, opacity: 1 }}
            viewport={{ once: true, margin: '-5%' }}
            transition={{ duration: reduceMotion ? 0 : 1.8, ease: 'easeInOut', opacity: { duration: 0.4 } }}
          />
        </svg>
      )}

      {/* ── Entry list ───────────────────────────────────────── */}
      <ul className="space-y-16">
        {experiences.map((exp, idx) => {
          const isEven = idx % 2 === 0;
          const IconComp = resolveIcon(exp);

          return (
            <li key={idx}>
              <motion.div
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: '-100px' }}
                variants={horizontalEntrance(isEven, 0.05 * idx)}
                className={`relative flex flex-col md:flex-row md:items-center ${
                  isEven ? 'md:justify-start' : 'md:justify-end'
                }`}
              >
                {/* ── Node dot (desktop) ─── */}
                <div
                  aria-hidden
                  ref={(el) => { nodeRefs.current[idx].current = el; }}
                  className="hidden md:block absolute left-1/2 -translate-x-1/2 z-10"
                >
                  {/* Glow ring */}
                  <span className="absolute inset-0 -m-1.5 rounded-full bg-emerald-400/20 scale-0 group-hover:scale-100 transition-transform duration-300" />
                  <span className="block w-3.5 h-3.5 rounded-full bg-gradient-to-br from-emerald-400 to-cyan-500 border-[3px] border-white shadow-md" />
                </div>

                {/* ── Mobile left-edge dot ─── */}
                <div
                  aria-hidden
                  ref={idx === 0 || !nodeRefs.current[idx].current
                    ? undefined
                    : undefined /* mobile dot is decorative only */}
                  className="md:hidden absolute left-5 -translate-x-1/2 top-6 z-10 w-2.5 h-2.5 rounded-full bg-gradient-to-br from-emerald-400 to-cyan-500 border-2 border-white shadow"
                />

                {/* ── Card ─────────────────────────────────── */}
                <div className={`w-full pl-10 md:pl-0 md:w-5/12 ${isEven ? 'md:pr-14' : 'md:pl-14'}`}>
                  <ExperienceCard exp={exp} index={idx} reduceMotion={reduceMotion} />
                </div>

                {/* ── Industry icon (desktop, opposite side from card) ─── */}
                {IconComp && (
                  <div
                    aria-hidden
                    className={`hidden md:flex absolute items-center justify-center w-11 h-11 opacity-50 hover:opacity-80 transition-opacity duration-300`}
                    style={isEven
                      ? { right: 'calc(50% - 13rem)', left: 'auto' }
                      : { left: 'calc(50% - 13rem)', right: 'auto' }
                    }
                  >
                    <IconComp />
                  </div>
                )}
              </motion.div>
            </li>
          );
        })}

        {experiences.length === 0 && (
          <li>
            <p className="text-center text-gray-500 py-12 text-sm">
              {config.ui?.noExperienceLabel || 'No experience entries yet.'}
            </p>
          </li>
        )}
      </ul>
    </div>
  );
};

export default ExperienceTimeline;
